import React from "react";

const SUBMIT_LOCK_RELEASE_DELAY_MS = 800;

export interface UseSubmitLockOptions {
  /** Modal/Drawer offen. Beim Schließen wird der Riegel zurückgesetzt. */
  open: boolean;
  /** Ein Submit-Vorgang läuft bereits (confirmLoading/submitting). */
  loading: boolean;
  /**
   * Wenn gesetzt: native `submit`-Events des Formulars werden abgefangen und während
   * eines laufenden/gesperrten Submits unterdrückt. So fängt der Riegel auch
   * Enter-Submit und Doppel-Submit über den nativen htmlType="submit"-Button ab.
   */
  formId?: string;
  /** Submit-Pfad ist überhaupt sichtbar/aktiv (sonst kein Listener nötig). */
  submitVisible?: boolean;
  /** Submit ist zusätzlich fachlich gesperrt (Pflichtfelder ungültig o. Ä.). */
  submitDisabled?: boolean;
}

export interface UseSubmitLockResult {
  /** true, solange ein Submit läuft oder der synchrone Riegel gesetzt ist. */
  submitBlocked: boolean;
  /**
   * Setzt den synchronen Riegel. Gibt false zurück, wenn bereits gesperrt — der
   * Aufrufer darf dann NICHT submitten. Für onClick-basierte Submit-Buttons.
   */
  armSubmitLock: () => boolean;
}

/**
 * Synchrones Doppelklick-/Doppel-Submit-Auffangnetz für Modal- und Drawer-Footer.
 *
 * Ein `useState`-`loading`-Flag greift erst nach dem nächsten Render — zwei schnelle
 * Klicks (oder doppeltes Enter) laufen beide durch, bevor `loading` true ist. Der
 * Ref-basierte Riegel hier blockiert sofort beim zweiten Submit und löst sich erst
 * wieder, wenn der Vorgang beendet ist (bzw. nach kurzer Sicherheits-Verzögerung,
 * falls kein Loading-State mitgeführt wird).
 *
 * Gemeinsame Basis von {@link FormDrawer} und {@link ThemedModal}.
 */
export function useSubmitLock({
  open,
  loading,
  formId,
  submitVisible = true,
  submitDisabled = false,
}: UseSubmitLockOptions): UseSubmitLockResult {
  const [submitLocked, setSubmitLocked] = React.useState(false);
  const submitLockRef = React.useRef(false);
  const loadingRef = React.useRef(loading);
  const submitDisabledRef = React.useRef(submitDisabled);
  const releaseTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  React.useEffect(() => {
    submitDisabledRef.current = submitDisabled;
  }, [submitDisabled]);

  const clearReleaseTimer = React.useCallback(() => {
    if (releaseTimerRef.current == null) return;
    window.clearTimeout(releaseTimerRef.current);
    releaseTimerRef.current = null;
  }, []);

  const releaseSubmitLock = React.useCallback(() => {
    clearReleaseTimer();
    submitLockRef.current = false;
    setSubmitLocked(false);
  }, [clearReleaseTimer]);

  const armSubmitLock = React.useCallback(() => {
    if (submitLockRef.current) return false;
    submitLockRef.current = true;
    setSubmitLocked(true);
    return true;
  }, []);

  // Riegel beim Schließen sofort lösen; nach Submit-Ende (loading false) verzögert
  // lösen, damit ein direkt folgender Klick nicht erneut durchrutscht.
  React.useEffect(() => {
    if (!open) {
      releaseSubmitLock();
      return;
    }
    if (loading) {
      clearReleaseTimer();
      return;
    }
    if (!submitLocked) return;
    clearReleaseTimer();
    releaseTimerRef.current = window.setTimeout(releaseSubmitLock, SUBMIT_LOCK_RELEASE_DELAY_MS);
    return clearReleaseTimer;
  }, [clearReleaseTimer, loading, open, releaseSubmitLock, submitLocked]);

  // Nativer Submit-Capture-Handler: fängt Enter-Submit + Doppel-Submit über den
  // htmlType="submit"-Button ab.
  React.useEffect(() => {
    if (!open || !formId || !submitVisible) return;
    const formElement = document.getElementById(formId);
    if (!(formElement instanceof HTMLFormElement)) return;

    const handleSubmit = (event: SubmitEvent) => {
      if (loadingRef.current || submitLockRef.current || submitDisabledRef.current) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      armSubmitLock();
    };

    formElement.addEventListener("submit", handleSubmit, { capture: true });
    return () => formElement.removeEventListener("submit", handleSubmit, { capture: true });
  }, [armSubmitLock, formId, open, submitVisible]);

  return {
    submitBlocked: loading || submitLocked,
    armSubmitLock,
  };
}
